// import AlertsContext from 'common/contexts/alerts';
import useRouter from "common/hooks/use-router";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { UsersIcon } from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useAuth from "common/hooks/useAuth";
import AlertsContext from "common/providers/alerts";
import { Server } from "types/Server";
import ServerItem from "./ServerItem";
import SlaveServerForm from "./SlaveServerForm";

function Servers() {
  const isMasterServer = import.meta.env.VITE_IS_MASTER_SERVER === "true";

  // For MASTER servers, show the full CRUD list
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { user: currentUser } = useAuth();
  const canManageServers = currentUser?.isSuperadmin;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Server>("servers", {
    defaultSort: "name:ASC",
    cachePrefix: `servers`,
    manual: true,
  });

  useEffect(() => {
    if (isMasterServer) {
      fetchItems();
    }
  }, [fetchItems, isMasterServer]);

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const { removeItem } = useItem<Server>("servers", null, {});

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e: any) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = useCallback(
    (id: any) => {
      navigate(`/servers/edit/${id}`);
    },
    [navigate]
  );

  const styleTop = "md:w-1/3 xl:flex-1 xl:w-auto";
  const styleBottom = "md:w-1/3 mt-2 xl:flex-1 xl:w-auto xl:mt-0";

  const columns = useMemo(
    () => [
      {
        Header: t("common.name"),
        filterName: "name$containsi",
        columnClasses: styleTop,
      },
    ],
    [t]
  ) as any;

  // For SLAVE servers, show only the license key form
  if (!isMasterServer) {
    return <SlaveServerForm />;
  }

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.servers")}
        subtitle={t("titleLegends.servers")}
        btnTitle={t("newItem.server")}
        btnSubtitle={t("newItem.serverLegend")}
        Icon={UsersIcon}
        filters={filters}
        setFilter={setFilter}
        addLink="/servers/add"
        addLinkDisabled={!canManageServers}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={ServerItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Servers;
